import { postWithAuth } from './common';
import { auth } from './firebase-config';
import { serverUrl } from './constants';

function sanitizePathPart(value) {
    return String(value || '').replace(/[^a-zA-Z0-9_.-]/g, '_');
}

function stripDecorators(value) {
    return String(value || '').split('#')[0].split('?')[0];
}

function getFileExtension(value) {
    const normalized = stripDecorators(value).trim().toLowerCase();
    const match = /\.[a-z0-9]+$/.exec(normalized);
    return match ? match[0] : '';
}

function inferMimeType(value) {
    const extension = getFileExtension(value);
    if (extension === '.jpg' || extension === '.jpeg') return 'image/jpeg';
    if (extension === '.png') return 'image/png';
    if (extension === '.webp') return 'image/webp';
    if (extension === '.heic') return 'image/heic';
    if (extension === '.heif') return 'image/heif';
    if (extension === '.pdf') return 'application/pdf';
    if (extension === '.mp4') return 'video/mp4';
    if (extension === '.mov') return 'video/quicktime';
    return '';
}

function resolveUploadMetadata(file, fallbackName) {
    const providedName = String(file?.name || fallbackName || `upload-${Date.now()}`).trim();
    const inferredMimeType = String(file?.type || '').trim() || inferMimeType(providedName) || 'application/octet-stream';
    const extension = getFileExtension(providedName)
        || (inferredMimeType === 'image/jpeg' ? '.jpg' : '')
        || (inferredMimeType === 'image/png' ? '.png' : '')
        || (inferredMimeType === 'image/webp' ? '.webp' : '')
        || (inferredMimeType === 'image/heic' ? '.heic' : '')
        || (inferredMimeType === 'image/heif' ? '.heif' : '')
        || (inferredMimeType === 'application/pdf' ? '.pdf' : '')
        || (inferredMimeType === 'video/mp4' ? '.mp4' : '')
        || (inferredMimeType === 'video/quicktime' ? '.mov' : '');

    const baseName = providedName.replace(/\.[a-z0-9]+$/i, '') || `upload-${Date.now()}`;

    return {
        fileName: `${sanitizePathPart(baseName)}${extension}`,
        mimeType: inferredMimeType,
    };
}

async function requestUploadSession({ path, mimeType, orgId, ownerWorkerId = '' }) {
    try {
        const resp = await postWithAuth('signedUploadUrl', {
            path,
            contentType: mimeType || 'application/octet-stream',
            orgId,
            ownerWorkerId,
        });
        return resp?.data || resp;
    } catch (error) {
        throw new Error(`Upload session request failed: ${error?.message || 'Unknown error'}`);
    }
}

async function uploadFileViaFunction({ file, path, mimeType, orgId, ownerWorkerId = '' }) {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('User not logged in');
    }

    const token = await user.getIdToken();
    const response = await fetch(`${serverUrl}uploadStorageObject`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': mimeType || 'application/octet-stream',
            // Fetch headers are byte strings; encode paths containing worker/client names.
            'X-Upload-Path': encodeURIComponent(path),
            'X-Upload-Path-Encoding': 'uri-component',
            'X-Upload-Org-Id': orgId,
            ...(ownerWorkerId ? { 'X-Upload-Owner-Worker-Id': ownerWorkerId } : {}),
        },
        body: file,
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data?.err || 'Upload failed');
    }
    return data?.data || data;
}

async function downloadFileViaFunction({ path, orgId }) {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('User not logged in');
    }
    if (!orgId) {
        throw new Error('Organisation is required for file access');
    }

    const token = await user.getIdToken();
    const response = await fetch(`${serverUrl}downloadStorageObject`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            filePath: path,
            orgId,
        }),
    });

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.err || 'File could not be opened.');
    }

    return response.blob();
}

function isWebUrl(value) {
    return typeof value === 'string' && /^(https?:\/\/|data:)/i.test(value);
}

function normalizeStoragePath(value) {
    if (!value || typeof value !== 'string') return '';

    if (value.startsWith('gs://')) {
        const parts = value.replace('gs://', '').split('/');
        parts.shift();
        return parts.join('/');
    }

    if (value.includes('firebasestorage.googleapis.com')) {
        try {
            const url = new URL(value);
            const raw = url.pathname.split('/o/')[1] || '';
            return decodeURIComponent(raw);
        } catch {
            return '';
        }
    }

    return value.replace(/^\/+/, '');
}

function extractCandidates(file) {
    if (!file) return [];
    if (typeof file === 'string') return [file];
    return [
        file.url,
        file.filePath,
        file.storagePath,
        file.path,
        file.fullPath,
        file.path_,
        file?._location?.path_,
        file?._location?.path,
        file?.metadata?.fullPath,
        file?.ref?.fullPath,
        file.downloadURL,
    ].filter(Boolean);
}

function parseSecureUrlResponse(resp) {
    if (!resp) return '';
    if (typeof resp === 'string') return resp;
    if (typeof resp.url === 'string') return resp.url;
    if (typeof resp.signedUrl === 'string') return resp.signedUrl;
    if (typeof resp.data === 'string') return resp.data;
    if (typeof resp.data?.url === 'string') return resp.data.url;
    if (typeof resp.data?.signedUrl === 'string') return resp.data.signedUrl;
    return '';
}

export async function uploadFileToStorage({ file, path, mimeType = '', confidential = false, fallbackName = '', orgId = '', ownerWorkerId = '' }) {
    const { fileName, mimeType: resolvedMimeType } = resolveUploadMetadata(file, fallbackName);
    const uploadPath = path || fileName;
    if (!orgId) {
        throw new Error('Organisation is required for file uploads');
    }
    const session = await uploadFileViaFunction({
        file,
        path: uploadPath,
        mimeType: mimeType || resolvedMimeType,
        orgId,
        ownerWorkerId,
    });

    if (confidential) {
        return {
            name: fileName,
            url: session.path || uploadPath,
            filePath: session.path || uploadPath,
            confidential: true,
        };
    }

    return {
        name: fileName,
        url: session.url || session.path || uploadPath,
        filePath: session.path || uploadPath,
        confidential: false,
    };
}

export async function resolveStorageFileUrl(file, orgId = '') {
    if (!file) return '';

    const candidates = extractCandidates(file);
    const source = candidates[0] || '';
    if (!source) return '';

    if (isWebUrl(source)) {
        return source;
    }

    const normalizedCandidates = [
        ...new Set(candidates.map(normalizeStoragePath).filter(Boolean)),
    ];

    for (const path of normalizedCandidates) {
        try {
            if (!orgId) {
                throw new Error('Organisation is required for file access');
            }
            const resp = await postWithAuth('secureUrl', { filePath: path, orgId });
            const resolved = parseSecureUrlResponse(resp);
            if (resolved) return resolved;
        } catch {
            try {
                const blob = await downloadFileViaFunction({ path, orgId });
                return URL.createObjectURL(blob);
            } catch {
                // Try next candidate.
            }
        }
    }

    return '';
}
